import { FeaturesPopupData } from "api/interfaces/settingsInterface.interface";
import { useMemo, useState } from "react";
import * as s from "pages/manager/features/FeaturesPopupStyled";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// import required modules
import { Pagination } from "swiper/modules";

type Props = {
  onModal: () => void;
  data: FeaturesPopupData;
  onClickDontShowAgain?: () => void;
  isShowAgain?: boolean;
};

export default function FeaturesPopup(props: Props) {
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isChackedDontShow, setIsCheckedDontShow] = useState<boolean>(false);

  const swiperKey = useMemo(() => {
    return props.data.slides
      .map((slide) => `${slide.title}-${slide.url}`)
      .join("|");
  }, [props.data]);

  return (
    <BigPopupTemplate
      title={props.data.title}
      onModal={() => {
        if (
          isChackedDontShow &&
          props.onClickDontShowAgain &&
          props.isShowAgain
        ) {
          props.onClickDontShowAgain();
          return;
        }
        props.onModal();
      }}
      className="features-popup"
    >
      <s.ViewFeatures.Container>
        <Swiper
          key={swiperKey}
          onSwiper={setSwiperRef}
          onSlideChange={(swiper: SwiperType) => {
            setActiveIndex(swiper.activeIndex);
          }}
          pagination={{
            dynamicBullets: true,
          }}
          modules={[Pagination]}
          className="features-swiper"
          spaceBetween={10}
          style={{
            width: "100%",
          }}
        >
          {props.data.slides.map((slide, index) => {
            const slideKey = `${slide.title}-${slide.url}-${index}`;

            return (
              <SwiperSlide key={slideKey}>
                <s.ViewFeatures.Content>
                  {slide.url ? (
                    <img key={slide.url} src={slide.url} alt={slide.title} />
                  ) : (
                    <div className="no-image"></div>
                  )}

                  <s.ViewFeatures.SlideInfo>
                    <h3>{slide.title}</h3>
                    <p>{slide.description}</p>
                  </s.ViewFeatures.SlideInfo>
                </s.ViewFeatures.Content>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </s.ViewFeatures.Container>
      <s.ViewFeatures.SwiperButton
        className={`swiper-button-prev ${
          activeIndex === 0 ? "swiper-button-disabled" : ""
        }`}
        onClick={() => swiperRef?.slidePrev()}
        data-testid="blocks-announcement-announcement-swiper-button-click"
      />

      <s.ViewFeatures.SwiperButton
        className={`swiper-button-next ${
          activeIndex === props.data.slides.length - 1
            ? "swiper-button-disabled"
            : ""
        }`}
        onClick={() => swiperRef?.slideNext()}
        data-testid="blocks-announcement-announcement-swiper-button-click-2"
      />

      {props.onClickDontShowAgain && props.isShowAgain && (
        <s.ViewFeatures.DontShowAgainButton>
          <div
            onClick={() => setIsCheckedDontShow(!isChackedDontShow)}
            data-testid="blocks-announcement-announcement-div-set-is-checked-dont-show"
          >
            <input type="checkbox" checked={isChackedDontShow} readOnly />
            <label>Don't show this announcement again</label>
          </div>
        </s.ViewFeatures.DontShowAgainButton>
      )}
    </BigPopupTemplate>
  );
}
